import { useState } from 'react'
import { CheckCircle2, Clock, Loader2, PencilLine, RefreshCw } from 'lucide-react'
import { useWorkspacePostStatus, type UpdateScheduledContentFn } from '@/lib/workspace-post-status'
import { getPlatformDisplayName } from '@/lib/calendar/types'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { Button } from '@/components/ui/shadcn/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'

/**
 * Lifecycle badge for the workspace editor header, scoped to the platform
 * whose caption tab is selected — switching tabs switches the badge, so it's
 * always clear which platforms have been published and which haven't.
 *
 * When the platform has a pending schedule whose baked content is older than
 * the workspace's last edit, an "Update content" button appears next to the
 * badge — edits never flow into a scheduled post silently.
 */
export function WorkspaceStatusBadge({
  workspaceId,
  platform,
  onUpdateScheduled,
  isSaving = false,
}: {
  workspaceId: number
  platform: PlatformType
  onUpdateScheduled?: UpdateScheduledContentFn
  /** Autosave in flight — re-syncing now would bake half-written content. */
  isSaving?: boolean
}) {
  const { getPlatformStatus, published, scheduled } = useWorkspacePostStatus(workspaceId)
  const [isUpdating, setIsUpdating] = useState(false)
  const status = getPlatformStatus(platform)

  const badge =
    status === 'published' ? (
      <div className="flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-0.5 text-sm text-green-800 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200">
        <CheckCircle2 size={14} />
        Published
      </div>
    ) : status === 'scheduled' ? (
      <div className="border-border bg-card text-foreground flex items-center gap-1.5 rounded-full border px-3 py-0.5 text-sm">
        <Clock size={14} />
        Scheduled
      </div>
    ) : (
      <div className="border-border bg-card text-muted-foreground flex items-center gap-1.5 rounded-full border px-3 py-0.5 text-sm">
        <PencilLine size={14} />
        Draft
      </div>
    )

  if (status === 'draft') return badge

  const platformPublished = published.filter((p) => p.platform === platform)
  const platformScheduled = scheduled.filter((s) => s.platform === platform)
  const drifted = platformScheduled.filter((s) => s.drifted)

  const detailLines = [
    ...platformPublished.map(
      (p) =>
        `Published ${new Date(p.publishedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}`,
    ),
    ...platformScheduled.map(
      (s) =>
        `Scheduled for ${new Date(s.scheduledAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`,
    ),
  ]

  const handleUpdate = async () => {
    if (!onUpdateScheduled || isUpdating || isSaving) return
    setIsUpdating(true)
    try {
      for (const entry of drifted) {
        await onUpdateScheduled(entry)
      }
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-sm">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">{getPlatformDisplayName(platform)}</p>
              {detailLines.map((line, i) => (
                <p key={i} className="text-sm">
                  {line}
                </p>
              ))}
              {status === 'published' && (
                <p className="text-sm">Duplicate the workspace to post again.</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>

        {drifted.length > 0 && onUpdateScheduled && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
                disabled={isUpdating || isSaving}
                onClick={handleUpdate}>
                {isUpdating || isSaving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RefreshCw size={14} />
                )}
                Update content
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm">
              <p className="text-sm">
                The workspace changed after this post was scheduled. The scheduled post still
                carries the old content — click to update it to what you see now.
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  )
}
